import {
  CLEARING_READINGS,
  currencySchema,
  MANUAL_PAYMENT_METHODS,
  MANUAL_PAYMENT_STAGES,
  PAYMENT_SLIP_MAX_BYTES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { listSearchSchema } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';
import {
  MANUAL_PAYMENT_CLEARING_STATUSES,
  openLineSchema,
  RECONCILIATION_STATUSES,
} from './reconciliation.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

const reasonSchema = z.string().trim().min(1).max(500);

const positiveMinorSchema = z
  .string()
  .regex(/^\d{1,18}$/, 'must be an integer in minor units')
  .refine((value) => /[1-9]/.test(value), 'must be above zero');

/** As many donations as a basket holds — one payment settles a basket at most. */
export const MAX_MANUAL_PAYMENT_DONATIONS = 50;

/**
 * Why the donations named cannot be paid together at all: one payment is one
 * line of one organization's account, in one currency.
 */
export const MANUAL_PAYMENT_PREPARE_REFUSALS = [
  'MANUAL_PAYMENT_MIXED_ORGANIZATIONS',
  'MANUAL_PAYMENT_MIXED_CURRENCIES',
  'MANUAL_PAYMENT_NO_ORGANIZATION',
] as const;

/** Why one donation cannot take a payment typed by hand. */
export const MANUAL_PAYMENT_DONATION_REFUSALS = [
  'MANUAL_PAYMENT_DONATION_NOT_ELIGIBLE',
  'MANUAL_PAYMENT_DONATION_CLOSED',
  'MANUAL_PAYMENT_NOTHING_OUTSTANDING',
] as const;

/**
 * Where a payment typed by hand stands: waiting for a second person, approved
 * — the donation is paid — or void, because it was rejected before approval or
 * withdrawn after it, which excludes its line (docs/decisions/0100).
 */
export const MANUAL_PAYMENT_STATES = ['pending_approval', 'approved', 'void'] as const;

export const MANUAL_PAYMENT_APPROVAL_BLOCKERS = ['recorder', 'slip_not_clean'] as const;

/**
 * What may be done about an approved payment, the server's answer from its
 * stage: `clear` finds its bank line (`reconciliation.match`), `withdraw` takes
 * it back (`reconciliation.unmatch`). The screen narrows them by the
 * permission each route checks; each write asks again.
 */
export const MANUAL_PAYMENT_ACTIONS = ['clear', 'withdraw'] as const;

/** Why `withdraw` would be refused now: a donation it settled holds an active receipt. */
export const MANUAL_PAYMENT_WITHDRAW_BLOCKERS = ['receipt_active'] as const;

/**
 * The awaiting-bank list's age filter: `overdue` is a payment waiting longer
 * than `MANUAL_PAYMENT_AWAITING_BANK_DAYS` for its statement line, `recent` one
 * within them.
 */
export const MANUAL_PAYMENT_AGE_BUCKETS = ['recent', 'overdue'] as const;

export const manualPaymentAccountSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  kind: z.enum(['bank', 'cash']),
  currency: currencySchema,
  /** The organization the account belongs to: a donation not yet paid may be paid into any linked organization's. */
  organization: z.object({ id: z.uuid(), name: z.string() }),
});

export type ManualPaymentAccount = z.infer<typeof manualPaymentAccountSchema>;

export const manualPaymentDonationSchema = z.object({
  donationId: z.uuid(),
  referenceCode: z.string(),
  donorName: z.string().nullable(),
  campaignName: z.string(),
  amount: moneySchema,
  /** What it still owes, less every confirmed and pending match: the amount a payment defaults to. */
  outstanding: moneySchema,
  /** A donation box's donation is paid by hand in cash and nothing else. */
  cashOnly: z.boolean(),
  refusal: z.enum(MANUAL_PAYMENT_DONATION_REFUSALS).nullable(),
});

export type ManualPaymentDonation = z.infer<typeof manualPaymentDonationSchema>;

/**
 * Everything the payment dialog needs before anything is written, read through
 * the caller's **donation** scope — the donations it names, what each owes,
 * and the active bank and cash accounts of their one organization in their one
 * currency, as `paymentLink.accounts` offers a donation its organization's
 * provider accounts.
 */
export const manualPaymentPreparationSchema = z.object({
  organization: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  currency: currencySchema.nullable(),
  donations: z.array(manualPaymentDonationSchema),
  accounts: z.array(manualPaymentAccountSchema),
  refusal: z.enum(MANUAL_PAYMENT_PREPARE_REFUSALS).nullable(),
});

export type ManualPaymentPreparation = z.infer<typeof manualPaymentPreparationSchema>;

export const recordManualPaymentInputSchema = z.object({
  method: z.enum(MANUAL_PAYMENT_METHODS),
  organizationAccountId: z.uuid(),
  /** The day the money changed hands — the slip's date, the cheque's, the day the cash was counted. */
  receivedOn: isoDaySchema,
  allocations: z
    .array(z.object({ donationId: z.uuid(), amountMinor: positiveMinorSchema }))
    .min(1)
    .max(MAX_MANUAL_PAYMENT_DONATIONS)
    .refine(
      (allocations) =>
        new Set(allocations.map((allocation) => allocation.donationId)).size === allocations.length,
      'a donation appears once in one payment',
    ),
  payerName: z.string().trim().max(200).optional(),
  reference: z.string().trim().max(100).optional(),
  note: z.string().trim().max(500).optional(),
  /** The slip's upload, required for a bank transfer and optional otherwise. */
  slipUploadToken: uploadTokenSchema.optional(),
  /**
   * The person has been told that an unmatched statement line of the same
   * amount already sits on this account, and records the payment anyway.
   */
  acknowledgeLineExists: z.boolean().optional(),
});

export type RecordManualPaymentInput = z.infer<typeof recordManualPaymentInputSchema>;

export const requestSlipUploadInputSchema = z.object({
  fileName: fileNameSchema,
  fileSize: z.number().int().min(1).max(PAYMENT_SLIP_MAX_BYTES),
});

export type RequestSlipUploadInput = z.infer<typeof requestSlipUploadInputSchema>;

export const manualPaymentSchema = z.object({
  /** The line the payment wrote; a payment and its line are one thing. */
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationAccountId: z.uuid(),
  accountLabel: z.string(),
  method: z.enum(MANUAL_PAYMENT_METHODS),
  receivedOn: isoDaySchema,
  amount: moneySchema,
  payerName: z.string().nullable(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  state: z.enum(MANUAL_PAYMENT_STATES),
  recordedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  recordedAt: z.string(),
  /** Why it is void — the rejection or the withdrawal — when it is. */
  voidReason: z.string().nullable(),
  slip: z
    .object({
      id: z.uuid(),
      fileName: z.string(),
      contentType: z.string(),
      scanStatus: z.enum(['pending', 'clean', 'infected', 'failed']),
    })
    .nullable(),
  allocations: z.array(
    z.object({
      matchId: z.uuid(),
      donationId: z.uuid(),
      referenceCode: z.string(),
      donorName: z.string().nullable(),
      amount: moneySchema,
      status: z.enum(RECONCILIATION_STATUSES),
    }),
  ),
  /**
   * Whether this reader may approve it: the server's answer to the four-eyes
   * rule, so the recorder is never offered an approval the write refuses.
   */
  recordedByReader: z.boolean(),
  /**
   * Why this reader may not approve it now, the server's answer: they typed it
   * or proposed one of its matches, or its slip has not scanned clean.
   */
  approvalBlockedBy: z.enum(MANUAL_PAYMENT_APPROVAL_BLOCKERS).nullable(),
  /** Where it stands, the bank's side included — `state` says only whether it is decided. */
  stage: z.enum(MANUAL_PAYMENT_STAGES),
  /** How much of it its statement line has been found for: nothing, or all of it. */
  cleared: moneySchema,
  /** Days since the money changed hands, while it waits for its bank line; null otherwise. */
  awaitingDays: z.number().int().min(0).nullable(),
  /** The second person, once it is approved. */
  approvedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  approvedAt: z.string().nullable(),
  /** The statement line it was found on, while that clearing stands. */
  clearing: z
    .object({ id: z.uuid(), bankTransactionId: z.uuid(), confirmedAt: z.string() })
    .nullable(),
  actions: z.array(z.enum(MANUAL_PAYMENT_ACTIONS)),
  withdrawBlockedBy: z.enum(MANUAL_PAYMENT_WITHDRAW_BLOCKERS).nullable(),
});

export type ManualPayment = z.infer<typeof manualPaymentSchema>;

/**
 * A payment typed from a slip or a cheque, found on its bank statement — the
 * statement line held for the payment's amount, and nothing else moved
 * (docs/decisions/0100).
 */
export const manualPaymentClearingSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  bankTransactionId: z.uuid(),
  manualTransactionId: z.uuid(),
  amount: moneySchema,
  status: z.enum(MANUAL_PAYMENT_CLEARING_STATUSES),
  confirmedAt: z.string(),
  reversedAt: z.string().nullable(),
  reversalReason: z.string().nullable(),
});

export type ManualPaymentClearing = z.infer<typeof manualPaymentClearingSchema>;

export const clearManualPaymentInputSchema = z.object({
  /** The payment — its own line. */
  id: z.uuid(),
  /** The statement line that brings its money. */
  bankTransactionId: z.uuid(),
  /** The payment's amount as the screen showed it; a payment that moved since is refused. */
  amountMinor: positiveMinorSchema,
});

export type ClearManualPaymentInput = z.infer<typeof clearManualPaymentInputSchema>;

export const manualPaymentListInputSchema = paginationInputSchema.extend({
  organizationAccountId: z.uuid().optional(),
});

export type ManualPaymentListInput = z.infer<typeof manualPaymentListInputSchema>;

export const manualPaymentAwaitingInputSchema = manualPaymentListInputSchema.extend({
  age: z.enum(MANUAL_PAYMENT_AGE_BUCKETS).optional(),
  /** A donation's code, the payer's name or the slip's number. */
  search: listSearchSchema,
});

export type ManualPaymentAwaitingInput = z.infer<typeof manualPaymentAwaitingInputSchema>;

/**
 * A statement line that may bring an approved payment's money, found from the
 * payment's side: a credit on its own account in its currency, booked three
 * days before the day it names to fourteen after, with at least the payment's
 * amount left. `reading` is how the matcher would read it — the line names the
 * payment or is exactly its amount — and null for a line that does neither,
 * such as one transfer carrying several payments, which only a person can
 * tell (docs/decisions/0100).
 */
export const manualPaymentBankLineSchema = openLineSchema.extend({
  reading: z.enum(CLEARING_READINGS).nullable(),
  exactAmount: z.boolean(),
});

export type ManualPaymentBankLine = z.infer<typeof manualPaymentBankLineSchema>;

export const manualPaymentBankLinesInputSchema = paginationInputSchema.extend({ id: z.uuid() });

export type ManualPaymentBankLinesInput = z.infer<typeof manualPaymentBankLinesInputSchema>;

/**
 * A payment typed by hand — a transfer from its slip, a cheque, or cash —
 * against one or more donations of one organization, waiting for a second
 * person: docs/decisions/0100. Recording moves no money; approving is the
 * allocation writer's ordinary approval of every match the payment holds, in
 * one transaction.
 */
export const manualPaymentContract = {
  prepare: oc
    .route({ method: 'GET', path: '/manual-payments/preparation' })
    .errors(guarded)
    .input(
      z.object({
        donationIds: z.array(z.uuid()).min(1).max(MAX_MANUAL_PAYMENT_DONATIONS),
      }),
    )
    .output(manualPaymentPreparationSchema),

  /** A presigned `PUT` for the slip, as a statement's upload is issued — .claude/rules/file-uploads.md. */
  requestSlipUpload: oc
    .route({ method: 'POST', path: '/manual-payments/slip-uploads' })
    .errors(guarded)
    .input(requestSlipUploadInputSchema)
    .output(presignedUploadSchema),

  record: oc
    .route({ method: 'POST', path: '/manual-payments' })
    .errors(guarded)
    .input(recordManualPaymentInputSchema)
    .output(manualPaymentSchema),

  /** Payments waiting for a second person, of the organizations the caller's scope wholly holds. */
  pending: oc
    .route({ method: 'GET', path: '/manual-payments/pending' })
    .errors(guarded)
    .input(manualPaymentListInputSchema)
    .output(paginated(manualPaymentSchema)),

  /**
   * Approved transfers and cheques whose statement line has not been found,
   * the longest waiting first, of the organizations the caller's scope wholly
   * holds — the reconciliation screen's *Banka hareketi bekleyen* tab.
   */
  awaiting: oc
    .route({ method: 'GET', path: '/manual-payments/awaiting-bank' })
    .errors(guarded)
    .input(manualPaymentAwaitingInputSchema)
    .output(paginated(manualPaymentSchema)),

  /** The statement lines an approved payment's money may have arrived on, strongest first. */
  bankLines: oc
    .route({ method: 'GET', path: '/manual-payments/{id}/bank-lines' })
    .errors(guarded)
    .input(manualPaymentBankLinesInputSchema)
    .output(paginated(manualPaymentBankLineSchema)),

  /** Approves every match of the payment, so a basket's donations are paid together or not at all. */
  approve: oc
    .route({ method: 'POST', path: '/manual-payments/{id}/approve' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(manualPaymentSchema),

  /** Declines it before approval: every match rejected, the line excluded with the reason. */
  reject: oc
    .route({ method: 'POST', path: '/manual-payments/{id}/reject' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(manualPaymentSchema),

  /**
   * Takes an approved payment back: every match unmatched, the ledger reversed
   * today, the line excluded. A donation holding an active receipt is refused
   * until the receipt is voided, as an unmatch is.
   */
  withdraw: oc
    .route({ method: 'POST', path: '/manual-payments/{id}/withdraw' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(manualPaymentSchema),

  /**
   * The payment's own statement line found: the line is held for the payment's
   * amount and nothing else moves — no ledger entry, no payment status, no
   * receipt — because the payment was counted when it was approved. One payment
   * at a time, by a person; never in bulk.
   */
  clear: oc
    .route({ method: 'POST', path: '/manual-payments/{id}/clearing' })
    .errors(guarded)
    .input(clearManualPaymentInputSchema)
    .output(manualPaymentClearingSchema),

  /** Undoes a clearing: the statement line given back, the payment awaiting its line again. */
  unclear: oc
    .route({ method: 'POST', path: '/manual-payment-clearings/{id}/reverse' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(manualPaymentClearingSchema),

  /** A 60-second link to the slip, audited before it is returned. */
  slipLink: oc
    .route({ method: 'GET', path: '/manual-payments/{id}/slip' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ url: z.string(), expiresAt: z.string() })),
};
